import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import "./rules.css";
import "../themeupdate/themeupdate.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { updateThemeData } from "../../functions/updateThemeData";

const MAX_RULES = 6;
const MAX_RULE_LEN = 50;
const MAX_TITLE_LEN = 80;
const MAX_CORRECT_POINTS = 100;
// Stored/sent to the backend as a negative number (0 to -50) — the game adds
// it to the score as-is, so a negative value is what actually deducts points.
// Admins type a plain positive amount to deduct; we negate it at the edges.
const MIN_WRONG_POINTS = -50;
const MAX_WRONG_POINTS = 0;
const MAX_WRONG_DEDUCTION = 50;

const NAMED_COLORS = {
  black: "#000000",
  white: "#ffffff",
  red: "#ff0000",
  green: "#008000",
  blue: "#0000ff",
  yellow: "#ffff00",
  gray: "#808080",
  grey: "#808080",
};

const ensureArray = (val) => {
  if (Array.isArray(val)) return val;
  if (val == null || val === "") return [];
  if (typeof val === "string") {
    try {
      const p = JSON.parse(val);
      return Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  }
  return [];
};

function toColorInputValue(val, fallback = "#000000") {
  if (!val || typeof val !== "string") return fallback;
  const v = val.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(v)) return v.toLowerCase();
  if (/^#[0-9a-fA-F]{8}$/.test(v)) return `#${v.slice(1, 7).toLowerCase()}`;
  const named = NAMED_COLORS[v.toLowerCase()];
  if (named) return named;
  return fallback;
}

const clampInt = (n, min, max, fallback) => {
  const x = parseInt(String(n), 10);
  if (Number.isNaN(x)) return fallback;
  return Math.min(max, Math.max(min, x));
};

/** Correct points: digits only, 0–100 */
const normalizeCorrectPointsInput = (raw) => {
  const digits = String(raw).replace(/\D/g, "");
  if (digits === "") return "";
  const n = parseInt(digits, 10);
  if (!Number.isFinite(n) || n < 0) return "0";
  if (n > MAX_CORRECT_POINTS) return String(MAX_CORRECT_POINTS);
  return String(n);
};

/** Wrong points deduction: digits only, 0–50 (admin types a plain positive amount) */
const normalizeWrongPointsInput = (raw) => {
  const digits = String(raw).replace(/\D/g, "");
  if (digits === "") return "";
  const n = parseInt(digits, 10);
  if (!Number.isFinite(n) || n < 0) return "0";
  if (n > MAX_WRONG_DEDUCTION) return String(MAX_WRONG_DEDUCTION);
  return String(n);
};

const Rules = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, data, status } = useSelector((state) => state.theme);

  const [loading, setLoading] = useState(false);
  const [rules, setRules] = useState([""]);
  const [title, setTitle] = useState("");
  const [correctPoints, setCorrectPoints] = useState("1");
  const [wrongPoints, setWrongPoints] = useState("0");
  const [textColor, setTextColor] = useState("#000000");
  const [bgColor, setBgColor] = useState("#1e293b");
  const [bgTextColor, setBgTextColor] = useState("#000000");

  useEffect(() => {
    console.log("[rules.jsx] mount-effect fetch, currentTheme=", currentTheme, new Date().toISOString());
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;
    console.log("[rules.jsx] data effect applying data.rules=", JSON.stringify(data.rules), "themename=", data.themename, "themeName=", data.themeName, new Date().toISOString());

    const r = ensureArray(data.rules);
    setRules(r.length ? r.map(String) : [""]);
    setTitle(String(data.landing_page_title ?? "").trim());
    setCorrectPoints(String(clampInt(data.points, 0, MAX_CORRECT_POINTS, 1)));
    setWrongPoints(String(Math.abs(clampInt(data.wrong_points, MIN_WRONG_POINTS, MAX_WRONG_POINTS, 0))));
    setTextColor(toColorInputValue(data.textcolor));
    setBgColor(toColorInputValue(data.box_color ?? data.boxcolor ?? "#1e293b"));
    setBgTextColor(toColorInputValue(data.button_Textcolor ?? data.button_text_color ?? "#000000"));
  }, [data]);

  const handleRuleChange = (idx, val) => {
    if (val.length > MAX_RULE_LEN) {
      Swal.fire("Limit", `Each rule can be at most ${MAX_RULE_LEN} characters.`, "warning");
      val = val.slice(0, MAX_RULE_LEN);
    }
    const arr = [...rules];
    arr[idx] = val;
    setRules(arr);
  };

  const handleAddRule = () => {
    if (rules.length >= MAX_RULES) {
      Swal.fire("Limit", `Up to ${MAX_RULES} rules.`, "warning");
      return;
    }
    setRules([...rules, ""]);
  };

  const handleRemoveRule = (idx) => {
    if (rules.length <= 1) {
      Swal.fire("Required", "At least one rule is required.", "warning");
      return;
    }
    setRules(rules.filter((_, i) => i !== idx));
  };

  const validate = () => {
    const trimmed = rules.map((r) => r.trim()).filter(Boolean);
    if (trimmed.length === 0) return { ok: false, msg: "At least one rule is required." };
    for (const r of trimmed) {
      if (r.length > MAX_RULE_LEN) return { ok: false, msg: `Each rule must be ${MAX_RULE_LEN} characters or less.` };
    }

    const t = title.trim();
    if (t.length > MAX_TITLE_LEN) {
      return { ok: false, msg: `Title must be ${MAX_TITLE_LEN} characters or less.` };
    }

    const cp = clampInt(correctPoints, 0, MAX_CORRECT_POINTS, 0);
    // Admin types a positive deduction amount; store it negative so the game's
    // "add wrong_points to score" logic keeps deducting exactly as before.
    const wp = -clampInt(wrongPoints, 0, MAX_WRONG_DEDUCTION, 0);

    return {
      ok: true,
      payload: {
        rules: trimmed,
        landing_page_title: t,
        points: cp,
        wrong_points: wp,
        textcolor: textColor,
        box_color: bgColor,
        button_Textcolor: bgTextColor,
      },
    };
  };

  const handleSave = async () => {
    const v = validate();
    if (!v.ok) {
      Swal.fire("Error", v.msg, "error");
      return;
    }
    setLoading(true);
    try {
      console.log("[rules.jsx] handleSave POST payload=", JSON.stringify(v.payload), "currentTheme=", currentTheme, "BACKEND_URL=", process.env.REACT_APP_BACKEND_URL, new Date().toISOString());
      const postResult = await updateThemeData({
        payload: {
          data: v.payload,
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });
      console.log("[rules.jsx] handleSave POST response=", JSON.stringify(postResult), new Date().toISOString());
      const refetched = await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      console.log("[rules.jsx] handleSave refetch response rules=", JSON.stringify(refetched?.data?.rules), new Date().toISOString());
      await Swal.fire("Success", "Data updated", "success");
      navigate("/admin/themeupdate");
    } catch (e) {
      console.log("[rules.jsx] handleSave ERROR=", e, new Date().toISOString());
      Swal.fire("Error", e.message || "Failed to save", "error");
    } finally {
      setLoading(false);
    }
  };

  if (status === "loading" && !data) {
    return (
      <div className="rules-page-new">
        <div className="themeupdate-loading">Loading...</div>
      </div>
    );
  }

  return (
    <>
      <div className="back-button-holder">
        <button type="button" className="back-button" onClick={() => navigate("/admin")}>
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
      </div>

      <div className="themeupdate-row rules-php-layout">
        <div className="themeupdate-col rules-php-col">
          <div id="tour-rules-list" className="themeupdate-card rules-php-card rules-php-card--left">
            <div className="themeupdate-card-body rules-php-card-body">
              <h4 className="themeupdate-label rules-php-card-title">Rules</h4>
              <div className="themeupdate-rules-repeater">
                {rules.map((rule, i) => (
                  <div key={i} className="themeupdate-rule-row rules-php-repeater-row">
                    <textarea
                      className="themeupdate-input rules-php-textarea rules-php-repeater-textarea"
                      placeholder="Enter a rule"
                      maxLength={MAX_RULE_LEN}
                      value={rule}
                      onChange={(e) => handleRuleChange(i, e.target.value)}
                    />
                    <button
                      type="button"
                      className="themeupdate-btn themeupdate-btn-danger"
                      onClick={() => handleRemoveRule(i)}
                    >
                      <i className="fa-solid fa-times" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="themeupdate-btn themeupdate-btn-primary themeupdate-add-rule"
                  onClick={handleAddRule}
                >
                  <i className="fa-solid fa-plus" /> Add rule
                </button>
                <p className="themeupdate-muted">
                  <code>
                    Up to {MAX_RULES} rules, at least 1 required, {MAX_RULE_LEN} characters each
                  </code>
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="themeupdate-col rules-php-col">
          <div id="tour-rules-settings" className="themeupdate-card rules-php-card rules-php-card--right">
            <div className="themeupdate-card-body rules-php-card-body">
              <h4 className="themeupdate-label rules-php-card-title">Settings</h4>

              <div className="rules-php-marks-row">
                <label htmlFor="rules-title" className="rules-php-marks-label">
                  Title (heading) <span className="hs-char-count">{title.length}/{MAX_TITLE_LEN}</span>
                </label>
                <input
                  id="rules-title"
                  type="text"
                  className="themeupdate-input"
                  style={{ maxWidth: "100%", width: "100%" }}
                  maxLength={MAX_TITLE_LEN}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Game heading"
                />
              </div>

              <div className="rules-php-marks-row">
                <label htmlFor="rules-correct-points" className="rules-php-marks-label">
                  Correct points (0 – {MAX_CORRECT_POINTS})
                </label>
                <input
                  id="rules-correct-points"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  maxLength={3}
                  className="themeupdate-input rules-php-number"
                  value={correctPoints}
                  onChange={(e) => setCorrectPoints(normalizeCorrectPointsInput(e.target.value))}
                  onBlur={() => {
                    const n = parseInt(correctPoints, 10);
                    if (correctPoints === "" || Number.isNaN(n)) setCorrectPoints("0");
                    else setCorrectPoints(String(clampInt(n, 0, MAX_CORRECT_POINTS, 0)));
                  }}
                />
              </div>

              <div className="rules-php-marks-row">
                <label htmlFor="rules-wrong-points" className="rules-php-marks-label">
                  Wrong points deducted (0 – {MAX_WRONG_DEDUCTION})
                </label>
                <input
                  id="rules-wrong-points"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  maxLength={2}
                  className="themeupdate-input rules-php-number"
                  value={wrongPoints}
                  onChange={(e) => setWrongPoints(normalizeWrongPointsInput(e.target.value))}
                  onBlur={() => {
                    const n = parseInt(wrongPoints, 10);
                    if (wrongPoints === "" || Number.isNaN(n)) setWrongPoints("0");
                    else setWrongPoints(String(clampInt(n, 0, MAX_WRONG_DEDUCTION, 0)));
                  }}
                />
              </div>

              <div className="rules-php-marks-row rules-php-color-row">
                <label htmlFor="rules-text-color" className="rules-php-marks-label">
                  Text color
                </label>
                <input
                  id="rules-text-color"
                  type="color"
                  className="themeupdate-color-input rules-php-color-input"
                  value={textColor}
                  onChange={(e) => setTextColor(e.target.value)}
                />
              </div>

              <div className="rules-php-marks-row rules-php-color-row">
                <label htmlFor="rules-bg-color" className="rules-php-marks-label">
                  Button background color
                </label>
                <input
                  id="rules-bg-color"
                  type="color"
                  className="themeupdate-color-input rules-php-color-input"
                  value={bgColor}
                  onChange={(e) => setBgColor(e.target.value)}
                />
              </div>

              <div className="rules-php-marks-row rules-php-color-row">
                <label htmlFor="rules-bg-text-color" className="rules-php-marks-label">
                  Button text color
                </label>
                <input
                  id="rules-bg-text-color"
                  type="color"
                  className="themeupdate-color-input rules-php-color-input"
                  value={bgTextColor}
                  onChange={(e) => setBgTextColor(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div id="tour-rules-save" className="rules-action-holder">
        <button type="button" className="save-and-continue" onClick={handleSave} disabled={loading}>
          Save
        </button>
      </div>
    </>
  );
};

export default Rules;
